import { useState, useEffect, useCallback } from 'react';
import { XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '@/store/languageStore';
import { bookingApi, type EquipmentTag, type VehicleEquipmentItem } from '@/services/booking/bookingApi';
import toast from 'react-hot-toast';

interface VehicleEquipmentSectionProps {
  vehicleId: number;
  organizationId?: string | number;
}

export default function VehicleEquipmentSection({ vehicleId, organizationId }: VehicleEquipmentSectionProps) {
  const { t } = useTranslation();

  const [items, setItems] = useState<VehicleEquipmentItem[]>([]);
  const [allTags, setAllTags] = useState<EquipmentTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTagId, setSelectedTagId] = useState('');
  const [notes, setNotes] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [eq, tags] = await Promise.all([
        bookingApi.getVehicleEquipment(vehicleId),
        bookingApi.getEquipmentTags(
          organizationId != null && String(organizationId).trim()
            ? { organizationId: String(organizationId) }
            : undefined
        ),
      ]);
      setItems(eq);
      setAllTags(tags.filter((t) => t.isActive));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [vehicleId, organizationId]);

  useEffect(() => { load(); }, [load]);

  const availableTags = allTags.filter(
    (tag) => !items.some((item) => item.equipmentTagId === tag.id)
  );

  const handleAdd = async () => {
    if (!selectedTagId) return;
    setAdding(true);
    try {
      await bookingApi.addVehicleEquipment(vehicleId, {
        equipmentTagId: selectedTagId,
        notes: notes.trim() || undefined,
      });
      setSelectedTagId('');
      setNotes('');
      toast.success(t('common.saved'));
      load();
    } catch (e) {
      console.error(e);
      toast.error(t('common.error'));
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (item: VehicleEquipmentItem) => {
    try {
      await bookingApi.removeVehicleEquipment(vehicleId, item.id);
      toast.success(t('equipment.removed'));
      load();
    } catch (e) {
      console.error(e);
      toast.error(t('common.error'));
    }
  };

  if (loading) {
    return <p className="text-sm text-textSecondary py-2">{t('common.loading')}</p>;
  }

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-textDefault">{t('equipment.vehicleEquipment')}</h3>

      {items.length === 0 ? (
        <p className="text-sm text-textSecondary">{t('equipment.noEquipment')}</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {items.map((item) => (
            <span
              key={item.id}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium"
              title={item.notes || undefined}
            >
              {item.tagIcon && <span>{item.tagIcon}</span>}
              {item.tagName}
              {item.notes && (
                <span className="text-xs text-primary/60">({item.notes})</span>
              )}
              <button
                type="button"
                onClick={() => handleRemove(item)}
                className="ml-0.5 rounded-full hover:bg-primary/20 p-0.5 transition-colors"
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}

      {availableTags.length > 0 && (
        <div className="flex items-center gap-2 pt-1">
          <select
            value={selectedTagId}
            onChange={(e) => setSelectedTagId(e.target.value)}
            className="px-2 py-1.5 border border-border rounded bg-bgBase text-textDefault text-sm"
          >
            <option value="">{t('equipment.selectTag')}</option>
            {availableTags.map((tag) => (
              <option key={tag.id} value={tag.id}>
                {tag.icon ? `${tag.icon} ` : ''}{tag.name}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('equipment.notesPlaceholder')}
            className="px-2 py-1.5 border border-border rounded bg-bgBase text-textDefault text-sm w-40"
          />
          <button
            type="button"
            onClick={handleAdd}
            disabled={adding || !selectedTagId}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary text-white rounded text-sm hover:opacity-90 disabled:opacity-50"
          >
            <PlusIcon className="h-4 w-4" />
            {t('common.add')}
          </button>
        </div>
      )}
    </div>
  );
}
